import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Plus, Trash2, Pencil, Save, X, ArrowLeft } from "lucide-react";

interface CouncilMember {
  id: number;
  selectedMember: string;
  memberName: string;
  role: string;
  isEditing?: boolean;
}

const CouncilMembers: React.FC = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<CouncilMember[]>([
    {
      id: 1,
      selectedMember: "member1",
      memberName: "Nguyễn Văn A",
      role: "Chủ tịch",
      isEditing: false,
    },
    {
      id: 2,
      selectedMember: "member2",
      memberName: "Trần Thị B",
      role: "Phó chủ tịch",
      isEditing: false,
    },
    {
      id: 3,
      selectedMember: "member3",
      memberName: "Lê Văn C",
      role: "Thư ký",
      isEditing: false,
    },
    {
      id: 4,
      selectedMember: "member4",
      memberName: "Phạm Thị D",
      role: "Ủy viên",
      isEditing: false,
    },
  ]);

  const handleInputChange = (
    index: number,
    field: keyof CouncilMember,
    value: string
  ) => {
    setMembers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleEdit = (member: CouncilMember) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === member.id ? { ...m, isEditing: true } : m))
    );
  };

  const handleSave = (memberId: number) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, isEditing: false } : m))
    );
  };

  const handleCancel = (memberId: number) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, isEditing: false } : m))
    );
  };

  const handleDelete = (memberId: number) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa thành viên này?")) {
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    }
  };

  const addMember = () => {
    navigate("/management/council/add-member");
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <PageShell title="Danh sách thành viên hội đồng">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleGoBack}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Danh sách thành viên hội đồng
            </h1>
          </div>
        </div>

        {/* Council Information Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Thông tin hội đồng
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex">
                <label className="w-32 text-sm font-medium text-gray-700">
                  Tên hội đồng:
                </label>
                <span className="text-sm text-gray-900">
                  Hội đồng xét duyệt đề tài số 1 năm 2025
                </span>
              </div>
              <div className="flex">
                <label className="w-32 text-sm font-medium text-gray-700">
                  Loại hội đồng:
                </label>
                <span className="text-sm text-gray-900">
                  Hội đồng xét duyệt thuyết minh đề cương
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex">
                <label className="w-32 text-sm font-medium text-gray-700">
                  Ngày quyết định:
                </label>
                <span className="text-sm text-gray-900">20/07/2025</span>
              </div>
              <div className="flex">
                <label className="w-32 text-sm font-medium text-gray-700">
                  Lĩnh vực đánh giá:
                </label>
                <span className="text-sm text-gray-900">
                  Công nghệ thông tin và tự động hóa
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Members List Section */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Danh sách thành viên hội đồng
              </h2>
              <button
                onClick={addMember}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Thêm thành viên
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-2 text-sm font-bold text-black text-left w-16">
                    STT
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-sm font-bold text-black text-left">
                    Chọn thành viên hội đồng
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-sm font-bold text-black text-left">
                    Tên thành viên hội đồng
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-sm font-bold text-black text-left">
                    Vai trò
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-sm font-bold text-black text-center w-32">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-3 py-2 text-sm text-gray-900 text-center w-16">
                      {index + 1}
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      {member.isEditing ? (
                        <select
                          value={member.selectedMember}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "selectedMember",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">- Chọn thành viên hội đồng -</option>
                          <option value="member1">Nguyễn Văn A</option>
                          <option value="member2">Trần Thị B</option>
                          <option value="member3">Lê Văn C</option>
                          <option value="member4">Phạm Thị D</option>
                          <option value="member5">Hoàng Văn E</option>
                        </select>
                      ) : (
                        <span className="text-sm text-gray-900">
                          {member.selectedMember === "member1" &&
                            "Nguyễn Văn A"}
                          {member.selectedMember === "member2" && "Trần Thị B"}
                          {member.selectedMember === "member3" && "Lê Văn C"}
                          {member.selectedMember === "member4" && "Phạm Thị D"}
                          {member.selectedMember === "member5" && "Hoàng Văn E"}
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      {member.isEditing ? (
                        <input
                          type="text"
                          value={member.memberName}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "memberName",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập tên thành viên"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">
                          {member.memberName}
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2">
                      {member.isEditing ? (
                        <select
                          value={member.role}
                          onChange={(e) =>
                            handleInputChange(index, "role", e.target.value)
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">- Chọn vai trò -</option>
                          <option value="Chủ tịch">Chủ tịch</option>
                          <option value="Phó chủ tịch">Phó chủ tịch</option>
                          <option value="Thư ký">Thư ký</option>
                          <option value="Ủy viên">Ủy viên</option>
                          <option value="Phản biện">Phản biện</option>
                        </select>
                      ) : (
                        <span className="text-sm text-gray-900">
                          {member.role}
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center w-32">
                      <div className="flex justify-center space-x-2">
                        {member.isEditing ? (
                          <>
                            <button
                              onClick={() => handleSave(member.id)}
                              className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                              title="Lưu"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleCancel(member.id)}
                              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                              title="Hủy"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(member)}
                              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                              title="Sửa"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(member.id)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Xóa"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CouncilMembers;
